import * as Api from '@ViewModels';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { type IPhoneCall } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { type IPhoneCallFilterCriteria, PhoneCallFilterCriteriaProperty } from '../../viewModels/phonecall';

/**
 * Row shape returned by POST /phonecall/filter. Each entry wraps the full underlying phone call
 * (including `aiConversationId` and `coachingAdherenceResult`) plus resolved company/contact
 * references. We only need `phoneCall` here, but the optional fields are typed so the response
 * shape is honest.
 */
type PhoneCallView = {
	phoneCall: IPhoneCall;
	company?: { id?: string; companyName?: string };
	contacts?: Api.IContact[];
};

type Params = {
	conversationId: string | null;
	repUserId?: string;
	conversationDate?: string;
	enabled?: boolean;
};

const PAGE_SIZE = '100';
// Window (in days) on either side of the conversation's creation date used to scope the
// phonecall/filter query. The coaching call that produced a conversation is created within
// moments of the conversation itself, so a few days of slack is generous.
const DATE_WINDOW_DAYS = 2;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Resolves the scored `IPhoneCall` for a SalesLiveCoaching conversation.
 *
 * There is no `aiConversationId` filter criterion on the backend, and the `AIConversation`
 * itself carries no `phoneCallId`. The only client-reachable link is the reverse one:
 * `POST /phonecall/filter` returns rows whose `phoneCall` is the FULL model, including
 * `aiConversationId`. So we scope a query to the rep (the `User` criterion is REQUIRED by the
 * data store) plus a narrow date window around the conversation's creation date, then match
 * `phoneCall.aiConversationId === conversationId` client-side.
 *
 * LIMITATION (acceptable for v1): a phone call whose `Date` falls outside the
 * `creationDate ± DATE_WINDOW_DAYS` window, or that sorts beyond `PAGE_SIZE` rows within that
 * window, will not be found and the scorecard will simply not render. When `conversationDate`
 * is absent we fall back to a rep-only (User) filter, which is more susceptible to the
 * pageSize cutoff. A future backend `phoneCallId` on the conversation (or a filterable
 * `aiConversationId`) would make this exact and remove the heuristic entirely.
 */
export function usePhoneCallForConversation({
	conversationId,
	repUserId,
	conversationDate,
	enabled = true,
}: Params): UseQueryResult<IPhoneCall | null, Error> {
	const userSession = useUserSession();

	return useQuery<IPhoneCall | null, Error>({
		// `conversationDate` is part of the cache identity because it drives the ±window date filter
		// below — if the list refetches and a conversation's creationDate shifts, the window changes
		// and the cached result must not be reused.
		queryKey: ['phone-call-for-conversation', conversationId, repUserId, conversationDate],
		enabled: enabled && Boolean(conversationId) && Boolean(repUserId),
		queryFn: async () => {
			const criteria: IPhoneCallFilterCriteria[] = [
				{ property: PhoneCallFilterCriteriaProperty.User, value: repUserId as string },
			];

			const parsedDate = conversationDate ? Date.parse(conversationDate) : Number.NaN;
			if (!Number.isNaN(parsedDate)) {
				const start = new Date(parsedDate - DATE_WINDOW_DAYS * DAY_MS).toISOString();
				const end = new Date(parsedDate + DATE_WINDOW_DAYS * DAY_MS).toISOString();
				criteria.push({ property: PhoneCallFilterCriteriaProperty.Date, value: start, op: Api.FilterOperator.Gt });
				criteria.push({ property: PhoneCallFilterCriteriaProperty.Date, value: end, op: Api.FilterOperator.Lt });
			}

			const result = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<PhoneCallView>>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'phonecall/filter',
					queryParams: { pageSize: PAGE_SIZE },
				}),
				'POST',
				{ criteria }
			);

			const match = (result?.values ?? []).find(row => row.phoneCall?.aiConversationId === conversationId);
			return match?.phoneCall ?? null;
		},
		retry: false,
		staleTime: 60 * 1000,
		meta: {
			toastErrorMessage: 'Failed to load coaching analysis',
			errorLogging: { fn: 'usePhoneCallForConversation' },
		},
	});
}
